import { Box, Card, CardContent, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { StyledTableCell, StyledTableRow } from "Pages/AdminList/ProfessionalUserList";
import { useEffect, useMemo, useState } from "react";
import AdminApi from "Service/Admin.api";
import { Chip, keyframes, styled } from "@mui/material";
import ColorData from "Components/CstmTheme/ColorData";
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "../../Redux/Screen/Action";
import { formatDate, getStatusColorHex } from "Utils/Common";
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status?: string }>(({ theme, status }) => {
  const lowercaseStatus = status?.toLowerCase();

  let bgColor = theme.palette.grey[300];
  let textColor = theme.palette.text.primary;

  if (lowercaseStatus === "completed") {
    bgColor = theme.palette.success.main;
    textColor = theme.palette.success.contrastText;
  } else if (
    lowercaseStatus === "pending" ||
    lowercaseStatus === "initial consultation" ||
    lowercaseStatus === "initial assessment"
  ) {
    bgColor = theme.palette.warning.main;
    textColor = theme.palette.warning.contrastText;
  } else if (lowercaseStatus === "rejected") {
    bgColor = theme.palette.error.main;
    textColor = theme.palette.error.contrastText;
  }

  return {
    fontWeight: 600,
    borderRadius: theme.spacing(3),
    background: bgColor,
    color: textColor,
    animation: lowercaseStatus === "completed" ? `${pulse} 2s infinite` : undefined,
  };
});


// Dummy data
interface Order {
  id: number;
  pkCase: string;
  orderTypeName: string;
  caseTypeName: string;
  clientFullName: string;
  currentProcessingStatusName: string;
  statusLastUpdated: string;
  assignedProfessionalName: string;
  casePrice: number;
  casePaymentStatus: string;
}

const OrderHistory: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [ordersHistory, setOrdersHistory] = useState<Order[]>([]);
  const dispatch = useDispatch();
  useEffect(() => {
    fetchCasesByUserId();
  }, [])

  // OPTIMIZED SEARCH: filteredData is calculated on the fly
  const filteredData = useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase().trim();
    if (!normalizedQuery) return ordersHistory;

    return ordersHistory.filter((order) => {
      return (
        order.clientFullName?.toLowerCase().includes(normalizedQuery) ||
        order.pkCase?.toString().toLowerCase().includes(normalizedQuery) ||
        order.orderTypeName?.toLowerCase().includes(normalizedQuery) ||
        order.caseTypeName?.toLowerCase().includes(normalizedQuery) ||
        order.currentProcessingStatusName?.toLowerCase().includes(normalizedQuery) ||
        order.assignedProfessionalName?.toLowerCase().includes(normalizedQuery) ||
        formatDate(order.statusLastUpdated).toLowerCase().includes(normalizedQuery)
      );
    });
  }, [searchQuery, ordersHistory]);


  const fetchCasesByUserId = async () => {
    dispatch(startLoading(
      {
        loading: true,
        loadtext: "",
      }
    ))
    const persistedData = localStorage.getItem("persist:root");

    if (persistedData) {
      try {
        // Step 2: Parse root JSON
        const parsedRoot = JSON.parse(persistedData);

        // Step 3: Parse 'user' property which is itself a JSON string
        const userObj = JSON.parse(parsedRoot.user);

        // Step 4: Access userId (i.e. `id` inside `myInfo`)
        const userId = userObj?.myInfo?.id;
        const response = await AdminApi.OrderHistory(userId);
        if (response.success && Array.isArray(response.data)) {
          setOrdersHistory(response.data);
        } else {
          setOrdersHistory([]);
        }
        dispatch(stopLoading());

      } catch (error) {
        console.error("Failed to parse persisted user data:", error);
          setOrdersHistory([]);
      }
    }
  };



  const renderTable = () => (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
      }}
    >
      {/* Table Container with Scroll */}
      <TableContainer sx={{ maxHeight: '70vh', overflowY: 'auto'  }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>Case No.</StyledTableCell>
              <StyledTableCell>Order Type</StyledTableCell>
              <StyledTableCell>Case Type</StyledTableCell>
              <StyledTableCell>Assigned Professional</StyledTableCell>
              <StyledTableCell>Case Price</StyledTableCell>
              <StyledTableCell>Case Status</StyledTableCell>
              <StyledTableCell>Payment Status</StyledTableCell>
              <StyledTableCell>Created At</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="h6">No orders found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(filteredData) &&
              filteredData?.map((order) => (
                <StyledTableRow key={order.id}>
                  <TableCell>{order.pkCase}</TableCell>
                  <TableCell>{order.orderTypeName}</TableCell>
                  <TableCell>{order.caseTypeName}</TableCell>
                  <TableCell>{order.assignedProfessionalName}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{order.casePrice}</TableCell>
                  <TableCell>
                    <StatusChip
                      label={order.currentProcessingStatusName}
                      status={order.currentProcessingStatusName}
                    />
                  </TableCell>

                   <TableCell>
                    <StatusChip
                      label={order.casePaymentStatus || ""}
                      status={order.casePaymentStatus}
                      sx={{
                        background: getStatusColorHex(order.casePaymentStatus).bg,
                        color: getStatusColorHex(order.casePaymentStatus).text, 
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>{formatDate(order.statusLastUpdated)}</TableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );

  const renderCards = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {filteredData.length === 0 ? (
        <Typography variant="body1" align="center">
          No orders found
        </Typography>
      ) : (
        Array.isArray(filteredData) &&
        filteredData?.map((order) => (
          <Card key={order.id} variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Case No: {order.pkCase}
              </Typography>
              <Typography variant="body2">
                <strong>Order Type:</strong> {order.orderTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Case Type:</strong> {order.caseTypeName}
              </Typography>
              <Typography variant="body2">
                <strong>Client:</strong> {order.assignedProfessionalName}
              </Typography>
              <Typography variant="body2">
                <strong>Case Price:</strong> {order.casePrice}
              </Typography>
              <Typography variant="body2">
                <strong>Payment Status:</strong> {order.casePaymentStatus || ""}
              </Typography>
              <Typography variant="body2">
                <strong>Case Status:</strong> {order.currentProcessingStatusName}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(order.statusLastUpdated)}
              </Typography>
            </CardContent>
          </Card>
        ))
      )}
    </Box>
  );

  return (
    <Box
      p={3}
      width="100%"

    >
      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
        padding: 2,
        borderRadius: 2,
        backgroundColor: theme => ColorData.primary.main,
        color: theme => ColorData.text.secondary,
      }}>
        Order History
      </Typography>
      <TextField
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search order history..."
        size="small"
        sx={{ width: { xs: '100%', sm: '300px', padding: 6 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      {isMobile ? renderCards() : renderTable()}
    </Box>
  );
};

export default OrderHistory;
